import React from "react";
import {
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    Chip,
    makeStyles
} from "@material-ui/core";
import {
    AttachFile as AttachFileIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
    listItem: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    unread: {
        backgroundColor: theme.palette.action.selected,
        fontWeight: "bold",
    },
    subject: {
        fontWeight: (props) => (props.isUnread ? "bold" : "normal"),
    },
    snippet: {
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    date: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
    },
    attachmentIcon: {
        fontSize: "1rem",
        marginLeft: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    starIcon: {
        cursor: "pointer",
        marginLeft: theme.spacing(1),
    },
}));

const EmailListItem = ({ email, onClick, onToggleStar }) => {
    const isUnread = email.flags && !email.flags.includes("\\Seen");
    const isStarred = email.flags && email.flags.includes("\\Flagged");
    const classes = useStyles({ isUnread });

    const formatDate = (date) => {
        try {
            const parsedDate = typeof date === "string" ? parseISO(date) : date;
            const now = new Date();
            const diff = now - parsedDate;
            const days = diff / (1000 * 60 * 60 * 24);

            if (days < 1) {
                return format(parsedDate, "HH:mm", { locale: ptBR });
            } else if (days < 7) {
                return format(parsedDate, "EEE", { locale: ptBR });
            } else {
                return format(parsedDate, "dd/MM/yyyy", { locale: ptBR });
            }
        } catch (error) {
            return "";
        }
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const parts = name.split(" ");
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const extractName = (emailString) => {
        // Extrair nome de "Nome <email@example.com>" ou retornar o email
        const match = emailString.match(/^(.+?)\s*<.+>$/);
        return match ? match[1].trim() : emailString.split("@")[0];
    };

    return (
        <ListItem
            button
            className={`${classes.listItem} ${isUnread ? classes.unread : ""}`}
            onClick={() => onClick(email)}
        >
            <ListItemAvatar>
                <Avatar>{getInitials(extractName(email.from))}</Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2" className={classes.subject}>
                            {extractName(email.from)}
                        </Typography>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            {email.hasAttachments && (
                                <AttachFileIcon className={classes.attachmentIcon} />
                            )}
                            {isStarred ? (
                                <StarIcon
                                    className={classes.starIcon}
                                    color="primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleStar(email, false);
                                    }}
                                />
                            ) : (
                                <StarBorderIcon
                                    className={classes.starIcon}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleStar(email, true);
                                    }}
                                />
                            )}
                            <Typography className={classes.date}>
                                {formatDate(email.date)}
                            </Typography>
                        </div>
                    </div>
                }
                secondary={
                    <>
                        <Typography
                            component="span"
                            variant="body2"
                            className={classes.subject}
                            color="textPrimary"
                        >
                            {email.subject}
                        </Typography>
                        {email.snippet && (
                            <Typography
                                component="span"
                                variant="body2"
                                className={classes.snippet}
                                display="block"
                            >
                                {email.snippet}
                            </Typography>
                        )}
                    </>
                }
            />
        </ListItem>
    );
};

export default EmailListItem;
